import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@provise/stores/user'
import Home from '@/views/home.vue'
import Login from '@provise/views/login.vue'
import SsoLogin from '@provise/views/login/sso.vue'
import config from '@/config'
import Complaint from '@/views/complaints/item.vue'
import Complaints from '@/views/complaints/list.vue'

export const complaintDefaultStep = 'registration'

const router = createRouter({
  history: createWebHistory(config.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: {
        title: 'Сводка',
        navbar: true,
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/complaints',
      name: 'complaint.list',
      component: Complaints,
      meta: {
        title: 'Обращения',
        navbar: true,
        icon: 'flag-fill',
      },
    },
    {
      path: '/complaint',
      name: 'complaint.create',
      component: Complaint,
      meta: {
        title: 'Обращение',
      },
    },
    {
      path: '/complaint/:id(\\d+)',
      name: 'complaint.item',
      props: propsId,
      redirect: to => ({
        name: 'complaint.item.step',
        params: { id: to.params.id, step: complaintDefaultStep },
      }),
      children: [
        {
          name: 'complaint.item.step',
          path: ':step',
          component: Complaint,
          props: p => ({ ...propsId(p), step: p.params.step }),
          children: [
            {
              name: 'complaint.item.communication',
              path: '/complaint/:id/:step#communication',
              component: Complaint,
              props: p => ({ ...propsId(p), step: p.params.step }),
            },
            {
              name: 'complaint.item.transfer',
              path: '/complaint/:id/:step#transfer',
              component: Complaint,
              props: p => ({ ...propsId(p), step: p.params.step }),
            },
          ],
        },
      ],
      meta: {
        title: 'Обращение',
      },
    },
    {
      path: '/catalog',
      name: 'catalog.list',
      meta: {
        title: 'Справочники',
        icon: 'file-earmark-medical',
        navbar: true,
        rbac: 'catalog_view',
      },
      children: [
        {
          path: '/customer_categories',
          name: 'customer_category.list',
          component: () => import('@/views/customer_category/list.vue'),
          meta: {
            title: 'Источники обращений',
            navbar: true,
            rbac: 'customer_category_view',
          },
        },
        {
          path: '/customer_category/:id(\\d+)?',
          name: 'customer_category.item',
          component: () => import('@/views/customer_category/item.vue'),
          props: propsId,
          meta: { title: 'Категория' },
        },
        {
          path: '/request_categories',
          name: 'request_category.list',
          component: () => import('@/views/request_category/list.vue'),
          meta: {
            title: 'Категории обращений',
            navbar: true,
            rbac: 'request_category_view',
          },
        },
        {
          path: '/request_category/:id(\\d+)?',
          name: 'request_category.item',
          component: () => import('@/views/request_category/item.vue'),
          props: propsId,
          meta: { title: 'Категория' },
        },
        {
          path: '/doc_templates',
          name: 'doc_templates.list',
          component: () => import('@/views/doc_templates/list.vue'),
          meta: {
            title: 'Бланки документов',
            navbar: true,
            rbac: 'doc_templates_view',
          },
        },
        {
          path: '/doc_templates/:id(\\d+)?',
          name: 'doc_templates.item',
          component: () => import('@/views/doc_templates/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Шаблон документа' },
        },
        {
          path: '/email_templates',
          name: 'email_templates.list',
          component: () => import('@/views/email_templates/list.vue'),
          meta: {
            title: 'Шаблоны ответов',
            navbar: true,
            rbac: 'email_templates_view',
          },
        },
        {
          path: '/email_templates/:id(\\d+)?',
          name: 'email_templates.item',
          component: () => import('@/views/email_templates/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Шаблон ответа' },
        },
        {
          path: '/catalog/medicine_trademarks',
          name: 'catalog.medicine_trademarks.list',
          component: () => import('@/views/catalog/medicine_trademarks/list.vue'),
          meta: {
            title: 'Торговое наименование',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicine_trademarks/:id(\\d+)?',
          name: 'catalog.medicine_trademarks.item',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Торговое наименование' },
        },
        {
          path: '/catalog/medicines',
          name: 'catalog.medicines.list',
          component: () => import('@/views/catalog/medicines/list.vue'),
          meta: {
            title: 'Лекарственные препараты',
            navbar: true,
            rbac: 'catalog_view',
          },
        },
        {
          path: '/catalog/medicines/:id(\\d+)?',
          name: 'catalog.medicines.item',
          component: () => import('@/views/catalog/medicines/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Лекарственные препарат' },
        },
        {
          path: '/departments',
          name: 'departments.list',
          component: () => import('@/views/departments/list.vue'),
          props: { type: 'department' },
          meta: {
            title: 'Подразделения',
            navbar: true,
            rbac: 'departments_view',
          },
        },
        {
          path: '/govs',
          name: 'govs.list',
          component: () => import('@/views/departments/list.vue'),
          props: { type: 'gov' },
          meta: {
            title: 'Государственные органы',
            navbar: true,
            rbac: 'departments_view',
          },
        },
        {
          path: '/providers',
          name: 'providers.list',
          component: () => import('@/views/departments/list.vue'),
          props: { type: 'provider' },
          meta: {
            title: 'Провайдеры услуг',
            navbar: true,
            rbac: 'departments_view',
          },
        },
        {
          path: '/meds',
          name: 'meds.list',
          component: () => import('@/views/departments/list.vue'),
          props: { type: 'med' },
          meta: {
            title: 'Медицинские учреждения',
            navbar: true,
            rbac: 'departments_view',
          },
        },
        {
          path: '/distributors',
          name: 'distributors.list',
          component: () => import('@/views/departments/list.vue'),
          props: { type: 'distributor' },
          meta: {
            title: 'Дистрибьюторы',
            navbar: true,
            rbac: 'departments_view',
          },
        },
        {
          path: '/department/:id(\\d+)?',
          name: 'departments.item',
          props: rt => ({ id: Number(rt.params.id), type: 'department' }),
          component: () => import('@/views/departments/item.vue'),
          meta: {
            title: 'Подразделение',
          },
        },
        {
          path: '/gov/:id(\\d+)?',
          name: 'govs.item',
          props: rt => ({ id: Number(rt.params.id), type: 'gov' }),
          component: () => import('@/views/departments/item.vue'),
          meta: {
            title: 'Государственный орган',
          },
        },
        {
          path: '/provider/:id(\\d+)?',
          name: 'providers.item',
          props: rt => ({ id: Number(rt.params.id), type: 'provider' }),
          component: () => import('@/views/departments/item.vue'),
          meta: {
            title: 'Провайдер услуг',
          },
        },
        {
          path: '/med/:id(\\d+)?',
          name: 'meds.item',
          props: rt => ({ id: Number(rt.params.id), type: 'med' }),
          component: () => import('@/views/departments/item.vue'),
          meta: {
            title: 'Медицинское учреждение',
          },
        },
        {
          path: '/distributor/:id(\\d+)?',
          name: 'distributors.item',
          props: rt => ({ id: Number(rt.params.id), type: 'distributor' }),
          component: () => import('@/views/departments/item.vue'),
          meta: {
            title: 'Дистрибьютор',
          },
        },
        {
          path: '/dropdowns',
          name: 'dropdowns.list',
          component: () => import('@/views/dropdowns/list.vue'),
          meta: {
            title: 'Выпадающие списки',
            navbar: true,
            rbac: 'sa',
          },
        },
        {
          path: '/users',
          name: 'users.list',
          component: () => import('@provise/views/users/list.vue'),
          meta: {
            title: 'Пользователи',
            navbar: true,
            rbac: 'users_edit',
          },
        },
        {
          path: '/users/:id(\\d+)?',
          name: 'users.item',
          component: () => import('@provise/views/users/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { title: 'Пользователь' },
        },
        {
          path: '/current-user/',
          name: 'profile.item',
          component: () => import('@provise/views/users/profile.vue'),
          meta: { title: 'Пользователь' },
        },
      ],
    },
    {
      path: '/analitycs',
      name: 'analitycs.list',
      redirect: { name: 'analitycs.report-1' },
      meta: {
        title: 'Аналитика',
        navbar: true,
        rbac: 'reports_view',
      },
      children: [
        {
          path: '/analitycs/report1',
          name: 'analitycs.report-1',
          component: () => import('@/views/analitycs/reports.vue'),
          meta: {
            title: 'Отчет 1',
            navbar: true,
          },
        },
        {
          path: '/analitycs/report2',
          name: 'analitycs.report-2',
          component: () => import('@/views/analitycs/reports.vue'),
          meta: {
            title: 'Отчет 2',
            navbar: true,
          },
        },
      ],
    },
    {
      path: '/sso_login',
      name: 'sso_login',
      component: SsoLogin,
    },
    {
      path: '/:notFoundPath(.*)',
      name: 'NotFound',
      component: () => import('@/views/notFound.vue'),
    },
  ],
  strict: true,
  scrollBehavior(to, from) {
    if (to.name === from.name) {
      return null
    }
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(to => {
  const { isAuthorized } = useUserStore()

  if (to.name === 'sso_login') {
    return true
  }

  if (isAuthorized === true && to.name === 'login') {
    return { name: 'home' }
  }

  if (isAuthorized === false && to.name !== 'login' && to.name !== 'sso_login') {
    return { name: 'login' }
  }
})

export default router

function propsId({ params: { id } }) {
  let _id = Number(id)
  if (isNaN(_id)) {
    _id = null
  }
  return { id: _id }
}
